import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { playSfxById, SfxId } from '@/sfx';
import {
  DRAG_HOTBAR_Z,
  HUD_TEXT_MUTED,
  HUD_Z,
  UI_GHOST_BG,
  UI_PANEL_BORDER,
} from '@/ui/consts';
import { useClient } from '@/ui/context';
import { skillStatus } from '@/skill-status';
import { skillHelp } from '@/skill-help';
import { SlotType } from '@/ui/enums';
import {
  useItemDrag,
  useItemGfxUrl,
  useSpellIconUrls,
} from '@/ui/in-game';

import { actionLabel, subscribeKeyBindings } from '@/keybindings';
import {bookSummonSlot,subscribeSummoning,summonCooldown,ownCompanion} from '@/summoning';
import summonIconUrl from '@/assets/summon-companion-v1.png';
const SLOT_SIZE = 'min(3rem, calc((100vw - 6rem) / 9))';
const SPELL_FRAME_W = 34;
const SPELL_FRAME_H = 32;

// ---------------------------------------------------------------------------
// Individual slot
// ---------------------------------------------------------------------------
type SlotProps = {
  index: number;
};

function HotBarSlot({ index }: SlotProps) {
  const client = useClient();
  const [slots, setSlots] = useState(() => [...client.hotbarSlots]);
  const [,keysChanged]=useState(0);
  useEffect(()=>subscribeKeyBindings(()=>keysChanged(n=>n+1)),[]);
  const { currentDrag } = useItemDrag();

  useEffect(() => {
    return client.hotbarController.subscribe(() => {
      setSlots([...client.hotbarSlots]);
    });
  }, [client]);
  useEffect(()=>index===0?subscribeSummoning(client,()=>setSlots([...client.hotbarSlots])):undefined,[client,index]);

  const summonId=bookSummonSlot(client,index);
  const slot = summonId===null ? slots[index] ?? { type: SlotType.Empty, typeId: 0 } : {type:SlotType.Skill,typeId:summonId};
  const isItem = slot.type === SlotType.Item;
  const isSkill = slot.type === SlotType.Skill;
  const isSummon = isSkill && slot.typeId === 48;
  const [timing,setTiming] = useState({active:0,cooldown:0});
  useEffect(() => {
    if (!isSkill) {setTiming({active:0,cooldown:0});return;}
    const update = () => setTiming(summonId===null?skillStatus(client,slot.typeId):{active:0,cooldown:summonCooldown(client)});
    update();const timer=window.setInterval(update,250);
    return () => window.clearInterval(timer);
  },[client,isSkill,slot.typeId,summonId]);

  const itemGraphicId = isItem
    ? (client.getEifRecordById(slot.typeId)?.graphicId ?? null)
    : null;
  const spellIconId = isSkill && !isSummon
    ? (client.getEsfRecordById(slot.typeId)?.iconId ?? null)
    : null;

  const itemUrl = useItemGfxUrl(itemGraphicId, { shadow: true });
  const spellUrls = useSpellIconUrls(spellIconId);

  const [isSpellActive, setIsSpellActive] = useState(false);
  useEffect(() => {
    if (!isSkill) {
      setIsSpellActive(false);
      return;
    }
    const update = () =>
      setIsSpellActive(client.spellController.selectedSpellId === slot.typeId);
    client.spellController.subscribeSpellQueued(update);
    update();
    return () => {
      client.spellController.unsubscribeSpellQueued(update);
    };
  }, [client, isSkill, slot.typeId]);

  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const CLEAR_THRESHOLD = 30;

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (slot.type === SlotType.Empty) return;
      pointerStart.current = { x: e.clientX, y: e.clientY };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      playSfxById(SfxId.InventoryPickup);
    },
    [slot],
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      e.preventDefault();
      const start = pointerStart.current;
      pointerStart.current = null;
      if (!start || slot.type === SlotType.Empty) return;
      const dist = Math.hypot(e.clientX - start.x, e.clientY - start.y);
      if (dist >= CLEAR_THRESHOLD) {
        if(summonId!==null)return;
        client.hotbarController.clearSlot(index);
        playSfxById(SfxId.InventoryPlace);
      } else {
        client.spellController.useHotbarSlot(index);
      }
    },
    [client, slot.type, index,summonId],
  );

  const isDropTarget =
    summonId===null && !!currentDrag &&
    (currentDrag.source === 'inventory' || currentDrag.source === 'spell');

  return (
    <div
      class={`relative flex items-center justify-center overflow-visible rounded border ${UI_PANEL_BORDER} ${UI_GHOST_BG} transition-colors ${
        isDropTarget ? 'ring-2 ring-primary/60' : ''
      }`}
      style={{
        width: SLOT_SIZE,
        height: SLOT_SIZE,
        touchAction: 'none',
      }}
      data-hotbar-slot={summonId===null?index:undefined}
      data-summon-hotbar={summonId!==null?index:undefined}
      title={isSkill ? `${client.getEsfRecordById(slot.typeId)?.name ?? 'Skill'}: ${skillHelp(slot.typeId, client.getEsfRecordById(slot.typeId)?.name ?? '') ?? ''}` : undefined}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        pointerStart.current = null;
      }}
      onContextMenu={(e) => e.stopPropagation()}
    >
      {/* Key label */}
      <span
        class={`pointer-events-none absolute top-0.5 left-0.5 hidden select-none text-[0.5rem] leading-none lg:block ${HUD_TEXT_MUTED}`}
      >
        {actionLabel(`hotbar${index}`)}
      </span>

      {/* Item icon */}
      {isItem && itemUrl && (
        <img
          src={itemUrl}
          alt=''
          class='pointer-events-none relative z-10 max-h-full max-w-full object-contain p-0.5'
          draggable={false}
        />
      )}

      {isSkill && timing.cooldown > 0 && (
        <span class='pointer-events-none absolute bottom-0 right-0 z-20 rounded bg-black/80 px-1 text-[10px] text-white'
          title={timing.active>0?'Skill active':'Skill recovering'}>
          {timing.active>0?'ON ':''}{Math.ceil((timing.active || timing.cooldown)/1000)}s
        </span>
      )}
      {/* Spell icon */}
      {isSummon && (
        <img src={summonIconUrl} alt='Summon companion' data-summon-icon
          class={`pointer-events-none relative z-10 h-8 w-8 max-h-full max-w-full object-contain ${ownCompanion(client)?'drop-shadow-[0_0_2px_#67e8f9]':''}`}
          style={{imageRendering:'pixelated'}} draggable={false} />
      )}
      {isSkill && !isSummon && !spellUrls && (
        <span class='pointer-events-none text-xs' aria-label={client.getEsfRecordById(slot.typeId)?.name ?? 'Skill'}>
          {(client.getEsfRecordById(slot.typeId)?.name ?? '?').split(' ').map(word => word[0]).join('').slice(0, 3)}
        </span>
      )}
      {isSkill && !isSummon && spellUrls && (
        <img
          src={isSpellActive || timing.active > 0 || (summonId!==null&&ownCompanion(client)) ? spellUrls.active : spellUrls.normal}
          alt=''
          class='pointer-events-none relative z-10 object-contain'
          style={{ width: `${SPELL_FRAME_W}px`, height: `${SPELL_FRAME_H}px` }}
          draggable={false}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HotBar
// ---------------------------------------------------------------------------
export function HotBar() {
  const [bank,setBank]=useState(0);
  const { currentDrag } = useItemDrag();
  const barRef = useRef<HTMLDivElement>(null);
  const isDragging = currentDrag !== null;

  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const update = () => {
      const h = el.getBoundingClientRect().height;
      // Both responsive bars are mounted; the hidden one must not reset spacing.
      if (h > 0) document.documentElement.style.setProperty('--hotbar-h', `${h}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={barRef}
      role='presentation'
      class='flex items-center gap-1 max-w-full'
      style={{ zIndex: isDragging ? DRAG_HOTBAR_Z : HUD_Z }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }}
      onContextMenu={(e) => e.stopPropagation()}
    >
      {Array.from({ length: 9 }, (_, i) => (
        <HotBarSlot key={bank*9+i} index={bank*9+i} />
      ))}
      <button type='button'
        class={`shrink-0 rounded border px-1 text-xs ${UI_PANEL_BORDER} ${UI_GHOST_BG} ${HUD_TEXT_MUTED}`}
        style={{minWidth:'2.5rem',height:SLOT_SIZE,touchAction:'manipulation'}}
        data-hotbar-bank={bank}
        aria-label={`Show hotbar ${bank===0?2:1}`}
        title={bank===0?'Show bar 2 (Shift+1–9)':'Show bar 1 (1–9)'}
        onPointerDown={e=>e.stopPropagation()}
        onClick={()=>setBank(current=>1-current)}>
        {bank+1}/2 ↕
      </button>
    </div>
  );
}
